import * as angular from "angular"
import { FieldViewModel, ICrop, INMaxCropGroup, IParam, LayerTypeId, models } from "../../omnia"
import { ICroppingGroupItem, IDataLayer, INtrReq, INtrReqConfig, INutritionService, IRequirementsProvider, ReqConfigMode } from "../../types"
import { ConfigGroup } from "./config-vm-group"
import { cutOffOptions, NutrientGroupOptions } from "./options"

import ConfigMode = ReqConfigMode
import { isCropSupportedForNutrition } from "@omnia/shared/nutrition"
import { YieldLayerSubtype } from "@omnia/shared/generated/enums"

export class ConfigVm implements ICroppingGroupItem {

    static create(svc: INutritionService, cropping: FieldViewModel[], requirementsProvider: IRequirementsProvider, layers: IDataLayer[], lime: boolean): ConfigVm[] {

        const config = svc.getConfig();

        const crops = cropping.filter(c => c.currentYear && isCropSupportedForNutrition(c));

        const vm = crops.map(c => {

            let croppingLayers = layers.filter(l => l.FieldId == c.fieldId);

            let cfg = config.find(i => i.CroppingId == c.croppingId) || ConfigVm.createDefaultConfig(c, croppingLayers, lime);
            let reqs = requirementsProvider.getInitialRequirements(c.croppingId);
            let nmax = svc.getNMaxCroppingGroup(c.croppingId);

            return new ConfigVm(c, cfg, reqs, croppingLayers, nmax);

        });

        return vm;

    }

    private static createDefaultConfig(cropping: FieldViewModel, layers: IDataLayer[], lime: boolean): INtrReqConfig {

        const soilAnalysis = layers.some(l => l.TypeId == LayerTypeId.SoilAnalysis)
        const terramap = layers.some(l => l.TerraMap)

        const def = {

            NtrReqConfigId: 0,
            CroppingId: cropping.croppingId,

            NConfigMode: ConfigMode.WholeField,
            NRounding: 1,
            NMergeZones: false,

            PkConfigMode: (soilAnalysis || terramap) ? ConfigMode.SoilAnalysis : ConfigMode.WholeField,
            PkRounding: 5,
            PkMergeZones: false,
            CropOfftake: false,

            LimeConfigMode: (lime && (soilAnalysis || terramap)) ? ConfigMode.SoilAnalysis : ConfigMode.WholeField,
            LimeMergeZones: false,

        }

        return def

    }

    private _group: ConfigGroup;
    private _new: boolean

    public selected: boolean;

    public nConfigMode: ConfigMode;
    public nRounding: number;
    public nMergeZones: boolean;

    public pkConfigMode: ConfigMode;
    public pkRounding: number;
    public pkMergeZones: boolean;
    private _cropOfftake: boolean;

    public limeConfigMode: ConfigMode;
    public limeMergeZones: boolean;

    private _terraMap: boolean

    public lastUpdate: Date;

    private _hasSoilAnalysisLayer: boolean
    private _hasTargetYieldLayer: boolean;
    private _hasSoilTypeLayer: boolean;
    private _terramapLayers: boolean


    constructor(
        private croppingVm: FieldViewModel,
        private cfg: INtrReqConfig,
        private requirements: INtrReq[],
        layers: IDataLayer[],
        public nmaxGroup: INMaxCropGroup,
        public readonly authorized = croppingVm.authorized
    ) {

        this._new = !(cfg?.NtrReqConfigId)

        this.nConfigMode = cfg.NConfigMode
        this.nRounding = cfg.NRounding
        this.nMergeZones = cfg.NMergeZones

        this.pkConfigMode = cfg.PkConfigMode
        this.pkRounding = cfg.PkRounding
        this.pkMergeZones = cfg.PkMergeZones
        this.cropOfftake = cfg.CropOfftake

        this.limeConfigMode = cfg.LimeConfigMode
        this.limeMergeZones = cfg.LimeMergeZones

        this._hasSoilAnalysisLayer = layers.some(l => l.TypeId == LayerTypeId.SoilAnalysis);
        this._hasSoilTypeLayer = layers.some(l => (l.TypeId == LayerTypeId.SoilType) || (l.TypeId == LayerTypeId.SubsoilType));

        this._hasTargetYieldLayer = layers.some(l => checkYieldLayer(l, croppingVm.yearId, YieldLayerSubtype.TargetYield))

        this._terramapLayers = layers.some(l => l.TerraMap)

        this._terraMap = this._terramapLayers && (cfg.TerraMap || this._new)

        this.lastUpdate = cfg.UpdatedOn || cfg.CreatedOn;

        const checkMode = (mode: ConfigMode, filter: (m: ConfigMode) => boolean) => filter(mode) ? mode : ConfigMode.WholeField

        this.nConfigMode = checkMode(this.nConfigMode, m => this.filterNConfigMode(m))
        this.pkConfigMode = checkMode(this.pkConfigMode, m => this.filterPkConfigMode(m))
        this.limeConfigMode = checkMode(this.limeConfigMode, m => this.filterLimeConfigMode(m))

    }

    get group(): ConfigGroup {
        return this._group;
    }

    set group(value: ConfigGroup) {
        this._group = value;
    }

    get isNew() {
        return this._new
    }

    get cropping() {
        return this.croppingVm.cropping;
    }

    get field() {
        return this.croppingVm.field
    }

    get croppingId() {
        return this.croppingVm.croppingId;
    }

    get area() {
        return this.croppingVm.area
    }

    get crop(): ICrop {
        return this.croppingVm.crop;
    }

    get cropId(): number {
        return this.croppingVm.cropId;
    }

    get uncropped(): boolean {
        return (!this.crop) || (!this.crop.CropId);
    }

    get hasRequirements(): boolean {
        return !!(this.requirements && this.requirements.length);
    }

    get hasSoilAnalysisLayer() {
        return this._hasSoilAnalysisLayer
    }

    get hasTerraMapLayers() {
        return this._terramapLayers
    }

    get terraMap() {
        return this._terraMap
    }

    set terraMap(value) {

        this._terraMap = value

        if ((!value) && (this.pkConfigMode == ReqConfigMode.SoilAnalysis) && (!this._hasSoilAnalysisLayer)) {
            this.pkConfigMode = ReqConfigMode.WholeField
        }

        if (value && !usesLayers(this.pkConfigMode)) {
            this.pkConfigMode = ReqConfigMode.SoilAnalysis
        }

    }

    get nBreakEvenConfig() {
        return this.cfg.NBreakeven
    }

    get showTerraMapOption() {
        return this._terramapLayers
    }

    get cssColor() {
        return this.croppingVm.cssColor
    }

    get cropOfftake(): boolean {
        return this._cropOfftake;
    }

    set cropOfftake(value: boolean) {
        this._cropOfftake = value;
        if (value && (this.pkConfigMode == ConfigMode.SoilAnalysis)) {
            this.pkConfigMode = ConfigMode.WholeField
        }
    }

    getDescription(includeCrop: boolean): string {
        return this.croppingVm.getCroppingDescription(true, false, includeCrop);
    }

    getApiModel(): INtrReqConfig {

        let model = angular.copy(this.cfg)

        model.NConfigMode = this.nConfigMode
        model.PkConfigMode = this.pkConfigMode
        model.LimeConfigMode = this.limeConfigMode
        model.CropOfftake = this.cropOfftake
        model.TerraMap = this.showTerraMapOption && this._terraMap

        return model

    }

    getNConfigModeFilter() {
        return (p: IParam) => p ? this.filterNConfigMode(p.value) : false
    }

    filterNConfigMode(mode: ConfigMode) {
        switch (mode) {
            case ConfigMode.WholeField: return true
            case ConfigMode.Precision: return this._hasSoilTypeLayer || this._hasTargetYieldLayer
        }
    }

    getPkConfigModeFilter() {
        return (p: IParam) => p ? this.filterPkConfigMode(p.value) : false
    }

    filterPkConfigMode(mode: ConfigMode) {

        switch (mode) {

            case ConfigMode.WholeField:
                return true

            case ConfigMode.SoilAnalysis:
                if (this.cropOfftake) return false
                if (this._hasSoilAnalysisLayer) return true
                if (this._terramapLayers) return true
                return false

            case ConfigMode.Precision:
                return true

        }

    }

    getLimeConfigModeFilter(p: IParam) {
        return (p: IParam) => p ? this.filterLimeConfigMode(p.value) : false
    }

    filterLimeConfigMode(mode: ConfigMode) {
        switch (mode) {
            case ConfigMode.WholeField: return true
            case ConfigMode.SoilAnalysis: return this._hasSoilAnalysisLayer || this._terramapLayers
            case ConfigMode.Precision: return this._hasSoilTypeLayer || this._hasTargetYieldLayer
        }
    }

    getNutrientGroupMode(group: NutrientGroupOptions) {
        return <ConfigMode>(this[group.prefix + "ConfigMode"])
    }

    usesPrecision(includeLime: boolean) {
        if (this.nConfigMode == ConfigMode.Precision) return true
        if (this.pkConfigMode == ConfigMode.Precision) return true
        if (includeLime && (this.limeConfigMode == ConfigMode.Precision)) return true
    }

    getCutOffValue(key: (typeof cutOffOptions[number])["key"]): number {
        return this.cfg[key + "CutOff"]
    }

    hasCutOffOptions() {
        const { cfg } = this
        return Boolean(cfg.P2O5CutOff || cfg.K2OCutOff || cfg.MgOCutOff)
    }

}

function checkYieldLayer(l: IDataLayer, year: number, subtype: YieldLayerSubtype) {
    return (l.FirstYearId == year)
        && (l.TypeId == LayerTypeId.Yield)
        && ((l.SubtypeId || YieldLayerSubtype.TargetYield) == subtype)
}

function usesLayers(m: ReqConfigMode) {
    return (m == ReqConfigMode.SoilAnalysis) || (m == ReqConfigMode.Precision)
}
